import { FormEvent, useState } from "react";
import { ArrowRight, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { apiBaseUrl, siteConfig } from "../data/siteConfig";

type TopicType = "tax_prep" | "scope_check" | "general";

export function ContactForm() {
  const [topic, setTopic] = useState<TopicType>("tax_prep");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [renderedAt] = useState(() => Date.now());
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          topic,
          message: message.trim(),
          website: "",
          renderedAt,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || "Unable to send message. Please check your inputs or email us directly.");
      }

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong. Please try again or reach out directly.");
    }
  }

  function handleReset() {
    setName("");
    setEmail("");
    setPhone("");
    setMessage("");
    setTopic("tax_prep");
    setStatus("idle");
    setErrorMessage("");
  }

  if (status === "success") {
    return (
      <div className="contact-form-success" role="status" aria-live="polite">
        <CheckCircle2 size={44} className="contact-form-success__icon" aria-hidden="true" />
        <h3 className="contact-form-success__title">Message Received!</h3>
        <p className="contact-form-success__body">
          Thank you, <strong>{name}</strong>. Your inquiry has been sent directly to Gaurab Subedi.
          We will reply to <strong>{email}</strong> as soon as we can, usually within one business day.
        </p>
        <button
          className="button button--secondary contact-form-success__btn"
          type="button"
          onClick={handleReset}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="contact-website">Website</label>
        <input id="contact-website" name="website" tabIndex={-1} autoComplete="off" />
      </div>
      {status === "error" && (
        <div className="contact-form__error" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="contact-name">Your Full Name</label>
        <input
          id="contact-name"
          name="name"
          autoComplete="name"
          placeholder="e.g. Alex Johnson"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={status === "submitting"}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="contact-email">Email Address</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="alex@example.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "submitting"}
          />
        </div>
        <div className="form-group">
          <label htmlFor="contact-phone">Phone Number <span className="label-optional">(Optional)</span></label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder="(279) 555-0199"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={status === "submitting"}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Inquiry Topic</label>
        <div className="segmented-control" role="group" aria-label="Inquiry topic">
          <button
            type="button"
            className={topic === "tax_prep" ? "segmented-control__option is-active" : "segmented-control__option"}
            aria-pressed={topic === "tax_prep"}
            onClick={() => setTopic("tax_prep")}
            disabled={status === "submitting"}
          >
            Tax Preparation
          </button>
          <button
            type="button"
            className={topic === "scope_check" ? "segmented-control__option is-active" : "segmented-control__option"}
            aria-pressed={topic === "scope_check"}
            onClick={() => setTopic("scope_check")}
            disabled={status === "submitting"}
          >
            Scope / Eligibility
          </button>
          <button
            type="button"
            className={topic === "general" ? "segmented-control__option is-active" : "segmented-control__option"}
            aria-pressed={topic === "general"}
            onClick={() => setTopic("general")}
            disabled={status === "submitting"}
          >
            General Question
          </button>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="contact-message">How can we help you?</label>
        <textarea
          id="contact-message"
          name="message"
          rows={4}
          placeholder="Tell us about your tax situation (e.g. W-2, 1099, investment sales, questions on California deductions)..."
          required
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={status === "submitting"}
        />
      </div>

      <p className="form-notice">
        Please do not include your Social Security number, bank account numbers, or copies of tax
        documents in this form. You will upload those securely inside the client portal after we
        confirm we can help. Submitting this form does not create a preparer engagement. See our{" "}
        <a href="/privacy">Privacy Policy</a>.
      </p>

      <button
        className="button button--primary button--submit"
        type="submit"
        disabled={status === "submitting" || name.trim().length < 2 || email.trim().length < 5 || message.trim().length < 5}
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="animate-spin" size={17} aria-hidden="true" />
            <span>Sending Inquiry...</span>
          </>
        ) : (
          <>
            <span>Send Inquiry</span>
            <ArrowRight aria-hidden="true" size={17} strokeWidth={2.2} />
          </>
        )}
      </button>

      <p className="form-status" aria-live="polite">
        Prefer direct contact? Call or text <a href={`tel:${siteConfig.contactPhone.replace(/\D/g, "")}`}>{siteConfig.contactPhone}</a> or email <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>.
      </p>
    </form>
  );
}
